import { useEffect, useRef, useState } from 'react';
import { ErrorMessage, Field, useFormikContext } from 'formik';
import { isEmpty, sum } from 'lodash';
import { endOfMonth, startOfMonth, startOfToday } from 'date-fns';
import PropTypes from 'prop-types';
import SeaLinkCalendar from 'components/calendar';
import { useBulkQuery } from 'redux/api_slice/availability';
import { buildInvalidAndValidDates, formatDate } from './helpers';

const Calendar = ({ resource, initialDate }) => {
  const [today] = useState(() => startOfToday());
  const { values, setFieldValue, validateField } = useFormikContext();
  const { date } = values;
  const passengerCount = sum(Object.values(values.passengers).map(Number));
  const minPassenger = resource.minimum_passengers || 1;
  const maxPassenger = resource.maximum_passengers || Infinity;
  const [dateRange, setDateRange] = useState({});
  const disabled =
    passengerCount < minPassenger ||
    passengerCount > maxPassenger ||
    isEmpty(dateRange);
  const {
    data: bookability,
    isFetching,
    isSuccess,
    requestId,
  } = useBulkQuery(
    {
      resourceIds: [resource.id],
      units: passengerCount,
      fromDate: formatDate(dateRange.fromDate),
      toDate: formatDate(dateRange.toDate),
    },
    {
      skip: disabled,
    }
  );

  const [validDates, setValidDates] = useState([]);
  const [invalidDates, setInvalidDates] = useState([]);

  const calendarIns = useRef();
  // We automatically set the date to the first valid one ONLY for the first time
  const markOfSetFirstValidDate = useRef(false);

  const onMonthChange = ({ month }) => {
    const fromDate = startOfMonth(month);
    const toDate = endOfMonth(month);
    // Without below setTimeout, React pop up a warning saying we setState while rendering when we switch month.
    // Could be bug from mobiscroll
    setTimeout(() => {
      calendarIns.current?.navigate(month);
      setDateRange({ fromDate, toDate });
    });
  };

  const setCalendarValue = (value) => {
    setFieldValue('date', value);
    calendarIns.current?.setVal(value);
    onMonthChange({ month: new Date(value) });
  };

  const resetCalendarValue = () => {
    setFieldValue('date', undefined);
    calendarIns.current?.setVal(undefined);
    onMonthChange({ month: today });
  };

  useEffect(() => {
    // If passenger changes, clear the date in case it becomes invalid
    resetCalendarValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passengerCount]);

  useEffect(() => {
    if (initialDate) {
      setCalendarValue(initialDate);
    } else {
      resetCalendarValue();
      markOfSetFirstValidDate.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDate]);

  useEffect(() => {
    if (isSuccess && !isFetching) {
      const { validDates: newValidDates, invalidDates: newInvalidDates } =
        buildInvalidAndValidDates(bookability?.[resource.id]);

      setInvalidDates(newInvalidDates);
      setValidDates(newValidDates);
      // If the date is empty(meaning we dont have a default value)
      // We set it to the first valid date on that month.
      if (!date && newValidDates[0] && markOfSetFirstValidDate.current) {
        setFieldValue('date', newValidDates[0]);
        calendarIns.current.setVal(newValidDates[0]);
        markOfSetFirstValidDate.current = false;
      }
      validateField('date');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isFetching, requestId]);

  return (
    <div className="py-4 space-y-2 border-top">
      <label htmlFor="date" className="fw-bold">
        Date <sup className="text-xx-small text-danger">*</sup>
      </label>
      <div className="journey-dates__departure-wrapper">
        <div className="input-with-icon">
          <div className="input-with-icon__icon input-with-icon__icon--left">
            <i className="far fa-calendar-alt text-primary" />
          </div>
          <Field
            data-testid="date-input"
            component={SeaLinkCalendar}
            inputProps={{
              placeholder: 'Please select',
              readOnly: true,
              name: 'date',
              disabled: disabled || isFetching,
            }}
            onInit={(event, ins) => {
              calendarIns.current = ins;
            }}
            invalid={invalidDates}
            disabled={disabled || isFetching}
            onChange={({ value }) => {
              if (value) {
                const _value = formatDate(value);
                if (validDates.includes(_value)) {
                  setCalendarValue(_value);
                } else {
                  resetCalendarValue();
                }
              }
            }}
            onPageChange={onMonthChange}
            loading={isFetching}
          />
          <div className="input-with-icon__icon">
            <i
              className={
                isFetching
                  ? 'fas fa-spinner-third animation-momentum-spin me-2 text-body'
                  : 'fas fa-chevron-down text-body'
              }
            />
          </div>
        </div>
        <ErrorMessage
          name="date"
          component="div"
          className="text-danger text-x-small mt-1"
        />
      </div>
    </div>
  );
};

Calendar.propTypes = {
  initialDate: PropTypes.string,
  resource: PropTypes.shape({
    id: PropTypes.number,
    pricing: PropTypes.shape({
      // eslint-disable-next-line react/forbid-prop-types
      calendar: PropTypes.object,
    }),
    minimum_passengers: PropTypes.number,
    maximum_passengers: PropTypes.number,
  }),
};

export default Calendar;
